/**
 * Decide once the plan has gone in (screens §P6): not the editing page behind a "round isn't open" error, but what happened and the way
 * forward. Signed, the year running, then the results: "See results", and in a solo run "Continue to FY…" or "See your final".
 */
import type { ContentCatalog, RoundSummary, TeamState } from "@bsim/contracts";
import { callRequiredIn } from "@bsim/domain";
import { fiscalYearLabel } from "../format";
import { closedLines, reasonLines } from "../signoff/close";
import { GxStatusCard } from "../theme/GxStatusCard";
import { Link } from "../shell/history";
import { offersAdvance, RoundAdvance } from "../shell/RoundAdvance";

/** Rounds past the point where a plan can change: closed, running, resolved or released (a paused round keeps its editor, read-only). */
const SUBMITTED_STATES: ReadonlySet<RoundSummary["state"]> = new Set(["locked", "resolving", "awaitingSurprise", "resolvingPhase2", "resolved", "released"]);
export const isSubmitted = (round: RoundSummary): boolean => SUBMITTED_STATES.has(round.state);

/** The year stopped at a live surprise: the month it stopped at when the card says it. */
const surpriseHeadline = (t: { readonly phase: string; readonly month?: number } | undefined): string =>
  t?.phase === "midRound" && t.month !== undefined ? `The year is paused at month ${t.month} for a surprise.` : "The year is paused for a surprise.";

export function GxDecideSubmitted({ team, round, catalog }: { readonly team: TeamState; readonly round: RoundSummary; readonly catalog: ContentCatalog }) {
  const solo = team.cohort.mode === "solo";
  const released = round.state === "released";
  const lines = team.latestSubmission === null ? [] : closedLines(team.latestSubmission, callRequiredIn(round.roundNo));
  const waiting = round.soloPhase === "awaiting_event";
  const surprise = round.state === "awaitingSurprise" ? team.pendingEvents.find((e) => e.mode === "surprise") : undefined;
  const headline = released ? "Results are in." : round.state === "awaitingSurprise" ? surpriseHeadline(surprise?.timing) : waiting ? "The year is paused for an event." : solo ? "The year is running." : "Round closed. Results are being prepared.";
  return (
    <div className="gx-decide" data-testid="decide.page" data-submitted={released ? "released" : "running"}>
      <div className="gx-pagehead">
        <div>
          <div className="gx-eyebrow gx-num">Round {round.roundNo} · {fiscalYearLabel(catalog, round.roundNo)} · Submitted</div>
          <h1 className="gx-h1 gx-decide-h1">Decide</h1>
        </div>
      </div>
      <GxStatusCard icon={released ? "check" : waiting || round.state === "awaitingSurprise" ? "pause" : "clock"} tone={released ? "good" : "neutral"} testId="decide.submitted" title={headline}
        action={<>
          {released ? <Link href={`/c/${team.cohort.id}/results/${round.roundNo}`} className="gx-btn gx-btn--primary" data-testid="decide.seeresults">See results →</Link>
            : solo ? <Link href={`/solo/${team.cohort.id}`} className={`gx-btn ${waiting ? "gx-btn--primary" : "gx-btn--secondary"}`}>{waiting ? "Answer the event" : "Follow the year"} →</Link> : null}
          {solo && offersAdvance(team) ? <RoundAdvance team={team} catalog={catalog} beside={released} /> : null}
        </>}>
        {lines.map((l) => <span key={l} className="gx-status-line" data-testid="decide.closed">{l}</span>)}
        {team.latestSubmission === null ? null : reasonLines(team.latestSubmission, team.croAttestation).map((l) => <span key={l} className="gx-status-line gx-small gx-muted" data-testid="decide.reason">{l}</span>)}
      </GxStatusCard>
    </div>
  );
}
